import {
  certificateRegistryApi,
  CertificateRegistryApiTypes,
} from "@rshbintech.dboul/http-certificate-registry";
import {
  getCertificateList,
  Certificate as LocalCertificate,
  signCertificate,
} from "../cryptopro";
import { cadesplugin } from "../cryptopro/cadesplugin-api";
import {
  InvalidFileError,
  NotFoundMatchedCertificatesError,
  NotFoundRegisteredCertificateError,
} from "./Errors";

export class CryptoPro {
  public static async signFile(
    file: File
  ): Promise<{ signature: string; certificateSerialNumber: string }> {
    await cadesplugin();

    const fileHashContent = await CryptoPro.getBase64Content(file);
    const activeCertificate = await CryptoPro.getActiveCertificate();

    const signedMessage: string = await new Promise((resolve, reject) =>
      signCertificate(
        activeCertificate.serialNumber,
        fileHashContent,
        resolve,
        reject
      )
    );

    return {
      certificateSerialNumber: activeCertificate.serialNumber,
      signature: CryptoPro.removeLineBreaks(signedMessage),
    };
  }

  private static async convertBinaryToBase64(blob: Blob): Promise<string> {
    const fileReader = new FileReader();

    fileReader.readAsDataURL(blob);

    return new Promise((resolve, reject) => {
      fileReader.addEventListener("load", (event) => {
        const result = event.target?.result;

        if (typeof result !== "string") {
          reject(new InvalidFileError());

          return;
        }

        resolve(result);
      });

      fileReader.addEventListener("error", (error) => {
        reject(error);
      });
    });
  }

  private static async getActiveCertificate(): Promise<LocalCertificate> {
    try {
      const [localCertificates, userRegisteredCertificates] = await Promise.all(
        [
          CryptoPro.getLocalCertificateList(),
          CryptoPro.requestRegisteredCertificateList(),
        ]
      );

      let localActiveCertificate: LocalCertificate | null = null;

      for (let i = 0; i < userRegisteredCertificates.length; i++) {
        for (let j = 0; j < localCertificates.length; j++) {
          const localCertificate = localCertificates[j];
          const registeredCertificate = userRegisteredCertificates[i];

          if (localCertificate.serialNumber === registeredCertificate.serial) {
            localActiveCertificate = localCertificate;
          }
        }
      }

      if (localActiveCertificate === null) {
        throw new NotFoundMatchedCertificatesError();
      }

      return localActiveCertificate;
    } catch (error) {
      throw error;
    }
  }

  private static async getLocalCertificateList(): Promise<LocalCertificate[]> {
    const localCertificates: LocalCertificate[] = await new Promise(
      (resolve, reject) => {
        getCertificateList(resolve, reject);
      }
    );

    if (localCertificates.length === 0) {
      throw new NotFoundMatchedCertificatesError();
    }

    return localCertificates;
  }

  private static async requestRegisteredCertificateList(): Promise<
    CertificateRegistryApiTypes.ShortCertInfo[]
  > {
    try {
      const { data: registeredCertificates } =
        await certificateRegistryApi.api.getUserCertificates();

      if (registeredCertificates.length === 0) {
        throw new NotFoundRegisteredCertificateError();
      }

      return registeredCertificates;
    } catch (error) {
      throw error;
    }
  }

  private static async getBase64Content(file: File) {
    const base64Header = ";base64,";

    const fileHash = await CryptoPro.convertBinaryToBase64(file);
    const fileContentHash = fileHash.substring(
      fileHash.indexOf(base64Header) + base64Header.length
    );

    return fileContentHash;
  }

  private static removeLineBreaks(signature: string) {
    return signature.replace(/\r\n/g, "");
  }
}
